import React from 'react';
import { Box, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ListFooter from '../common/ListFooter';
import StatusChip from '../common/StatusChip';
import ResponsiveListTable from '../common/ResponsiveListTable';
import { cadenceLabel } from '../../utils/generateOccurrences';

// Map an action's type to a soft StatusChip status so chips read calmly, like Transactions
const typeStatus = { income: 'success', expense: 'error', transfer: 'info' };

// Larger page sizes now that rows are one line tall
export const ACTION_ROWS_OPTIONS = [10, 25, 50];

export default function ActionsCardList({
  actions = [], accounts = [], page, rowsPerPage, setPage, setRowsPerPage,
  onEdit, onDelete, sortBy, sortDirection, onSort,
  readOnly = false, selectedIds = [], onToggleSelect, onSelectAll
}) {
  const paginated = actions.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const getAccountName = (accountId) => {
    const a = accounts.find(x => x.id === accountId);
    return a ? a.name : 'All Accounts';
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);

  const amountColor = (action) =>
    action.type === 'income' ? 'success.main' : action.type === 'expense' ? 'error.main' : 'info.main';

  const amountText = (action) =>
    `${action.type === 'expense' ? '-' : action.type === 'income' ? '+' : ''}${formatCurrency(Math.abs(action.amount))}`;

  const accountText = (action) =>
    `${getAccountName(action.sourceAccountId)}${action.destinationAccountId ? ` → ${getAccountName(action.destinationAccountId)}` : ''}`;

  const scheduleText = (action) =>
    `${cadenceLabel(action.frequency, action.interval)}${action.endDate ? ` (to ${new Date(action.endDate).toLocaleDateString()})` : ''}`;

  const typeChip = (action) => (
    <StatusChip
      status={typeStatus[action.type] || 'info'}
      label={action.type.charAt(0).toUpperCase() + action.type.slice(1)}
      sx={{ fontSize: '0.75rem', height: 20 }}
    />
  );

  const activeChip = (action) => (
    <StatusChip
      status={action.isActive ? 'success' : 'neutral'}
      label={action.isActive ? 'Active' : 'Inactive'}
      sx={{ fontSize: '0.75rem', height: 20 }}
    />
  );

  return (
    <Box sx={{ opacity: readOnly ? 0.75 : 1 }}>
      <ResponsiveListTable
        items={paginated}
        getId={(a) => a.id || a._id}
        columns={[
          { id: 'name', label: 'Name', render: a => a.name || 'Unnamed Action', sx: { fontWeight: 600, maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis' } },
          { id: 'type', label: 'Type', sortable: false, render: typeChip },
          { id: 'schedule', label: 'Schedule', sortable: false, render: scheduleText, sx: { color: 'text.secondary' } },
          { id: 'account', label: 'Account', sortable: false, render: accountText, sx: { color: 'text.secondary', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' } },
          ...(readOnly ? [] : [{ id: 'status', label: 'Status', sortable: false, render: activeChip }]),
          {
            id: 'amount', label: 'Amount', align: 'right',
            render: a => (
              <Box component="span" sx={{ fontWeight: 700, color: amountColor(a) }}>{amountText(a)}</Box>
            )
          }
        ]}
        sortBy={sortBy}
        sortDirection={sortDirection}
        onSort={readOnly ? undefined : onSort}
        selectable={!readOnly}
        selectedIds={selectedIds}
        onToggleSelect={onToggleSelect}
        onSelectAll={onSelectAll}
        actions={readOnly ? [] : [
          { key: 'edit', label: 'Edit', icon: <EditIcon sx={{ fontSize: 18 }} />, onClick: (a) => onEdit && onEdit(a) },
          { key: 'delete', label: 'Delete', icon: <DeleteIcon sx={{ fontSize: 18 }} />, onClick: (a) => onDelete && onDelete(a), danger: true }
        ]}
        mobilePrimary={(a) => a.name || 'Unnamed Action'}
        mobileSecondary={(a) => `${scheduleText(a)} • ${accountText(a)}`}
        mobileChips={(a) => (
          <>
            {typeChip(a)}
            {!readOnly && activeChip(a)}
          </>
        )}
        mobileAmount={(a) => (
          <Typography sx={{ fontWeight: 700, fontSize: '0.8125rem', whiteSpace: 'nowrap' }} color={amountColor(a)}>
            {amountText(a)}
          </Typography>
        )}
      />
      {!readOnly && (
        <ListFooter
          page={page}
          setPage={setPage}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={setRowsPerPage}
          totalCount={actions.length}
          rowsOptions={ACTION_ROWS_OPTIONS}
        />
      )}
    </Box>
  );
}
